import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Shield,
  UsersThree,
  X
} from '@phosphor-icons/react';

const ROLES = [
  {
    value: 'admin',
    label: 'Admin',
    icon: Shield,
    description: 'Full access to manage users, workers, integrations, and organisation settings',
    color: 'text-[#EC4899]'
  },
  {
    value: 'member',
    label: 'Member',
    icon: UsersThree,
    description: 'Can run workers and workflows. Does not have access to manage users or settings',
    color: 'text-[#6366F1]'
  }
];

const ChangeRoleModal = ({ isOpen, member, onClose, onConfirm }) => {
  const [selectedRole, setSelectedRole] = useState(member?.role || 'member');

  if (!isOpen || !member) return null;

  const handleConfirm = () => {
    if (selectedRole !== member.role) {
      onConfirm(member, selectedRole);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative glass rounded-3xl p-8 max-w-md w-full card-glow"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Change Role
            </h2>
            <p className="text-[#A0AEC0] text-sm">
              Change role for <span className="text-white font-semibold">{member.user_name || member.email}</span>
            </p>
          </div>

          {/* Role Options */}
          <div className="space-y-3 mb-6">
            {ROLES.map((role) => {
              const Icon = role.icon;
              const isSelected = selectedRole === role.value;
              
              return (
                <button
                  key={role.value}
                  onClick={() => setSelectedRole(role.value)}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    isSelected
                      ? 'bg-[rgba(255,255,255,0.1)] border-2 border-[#6366F1]'
                      : 'glass border-2 border-transparent hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#6366F1]/20' : 'bg-[rgba(255,255,255,0.05)]'}`}>
                      <Icon size={20} className={role.color} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold">{role.label}</span>
                        {isSelected && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#6366F1] text-white">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-[#A0AEC0] text-xs leading-relaxed">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Agent Access Note */}
          <div className="mb-6 p-3 rounded-xl bg-[rgba(20,241,149,0.05)] border border-[rgba(20,241,149,0.2)]">
            <p className="text-xs text-[#A0AEC0] leading-relaxed">
              <span className="text-[#14F195] font-semibold">Note:</span> Agents are private by default. Only users you assign can see or run them.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedRole === member.role}
              className="flex-1 relative overflow-hidden rounded-full group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]"></div>
              <span className="relative z-10 flex items-center justify-center px-6 py-3 text-white font-semibold">
                Confirm
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ChangeRoleModal;
